import React, { FC, ReactNode } from 'react';

import {
  StepperDot,
  StepperItemButton,
  StepperItemLink,
  StepperItemStatic,
  StepperLabel,
  StepperRoot,
} from './styled';

export type StepperStepStatus = 'pending' | 'active' | 'done';

export type StepperStep = {
  label: ReactNode;
  status: StepperStepStatus;
  /** Custom dot content. Defaults to a check mark when `done` and the 1-based index otherwise. */
  dot?: ReactNode;
  /** Override the connector color leading to the next step. Defaults to `true` when `status === 'done'`. */
  doneConnector?: boolean;
  /** Renders the step as a react-router Link. Honored only for `done` steps. */
  to?: string;
  /** Renders the step as a button. Honored only for `done` steps. */
  onClick?: () => void;
  /** Override the default tooltip / aria-label hint for interactive steps. */
  ariaLabel?: string;
};

export type StepperProps = {
  steps: StepperStep[];
  className?: string;
  ariaLabel?: string;
};

const Stepper: FC<StepperProps> = ({
  steps,
  className,
  ariaLabel = 'Progress',
}) => {
  if (steps.length === 0) return null;

  return (
    <StepperRoot className={className} aria-label={ariaLabel}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isDone = step.status === 'done';
        const isActive = step.status === 'active';
        const doneConnector = step.doneConnector ?? isDone;
        const dotContent =
          step.dot !== undefined
            ? step.dot
            : isDone
            ? '\u2713'
            : String(index + 1);

        const dotEl = (
          <StepperDot
            data-stepper-dot
            $active={isActive}
            $done={isDone}
            aria-hidden={isDone}
          >
            {dotContent}
          </StepperDot>
        );
        const labelEl = (
          <StepperLabel $active={isActive} $done={isDone}>
            {step.label}
          </StepperLabel>
        );

        const sharedProps = {
          $isLast: isLast,
          $doneConnector: doneConnector,
          'aria-current': isActive ? ('step' as const) : undefined,
        };

        const interactiveAriaLabel =
          step.ariaLabel ??
          (typeof step.label === 'string' ? step.label : undefined);

        if (isDone && step.to) {
          return (
            <StepperItemLink
              key={`stepper-${index}`}
              to={step.to}
              aria-label={interactiveAriaLabel}
              {...sharedProps}
            >
              {dotEl}
              {labelEl}
            </StepperItemLink>
          );
        }

        if (isDone && step.onClick) {
          return (
            <StepperItemButton
              key={`stepper-${index}`}
              type="button"
              onClick={step.onClick}
              aria-label={interactiveAriaLabel}
              {...sharedProps}
            >
              {dotEl}
              {labelEl}
            </StepperItemButton>
          );
        }

        return (
          <StepperItemStatic key={`stepper-${index}`} {...sharedProps}>
            {dotEl}
            {labelEl}
          </StepperItemStatic>
        );
      })}
    </StepperRoot>
  );
};

export default Stepper;
